import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, catchError, map, of, switchMap } from 'rxjs';

import {
  AssistanceAudience,
  AssistanceModule,
  AssistanceResource,
} from '../../../../core/interfaces/assistance-resource';
import { AssistanceService } from '../../../../core/services/assistance.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { ExternalNavigationService } from '../../../../core/services/utils/external-navigation.service';
import {
  AUDIENCES,
  audienceIcon,
  audienceLabelKey,
  isAudience,
  kindIcon,
  resourceKind,
  summarize,
} from '../../../../core/utils/assistance-media';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import { AssistancePlayerComponent } from './assistance-player/assistance-player.component';
import { matchesSearch } from '../../../../core/utils/search-text';

interface ModuleEntry {
  module: AssistanceModule;
  /** Les ressources du module qui répondent à la recherche. */
  items: AssistanceResource[];
}

/**
 * La page d'aide en lecture — la même pour tous les rôles, sans aucune action
 * d'édition.
 *
 * <p>Un professeur voit les modules des professeurs, un élève ceux des élèves.
 * L'équipe voit les siens, et peut regarder ceux des autres publics tels
 * qu'ils les voient (`?as=Professor`) : ce qu'elle prévisualise est donc la page
 * livrée, pas une copie qui pourrait diverger. Seule l'équipe lit ce paramètre ;
 * pour tout autre rôle, il est ignoré — et le serveur ferme de toute façon
 * chaque public à qui n'en fait pas partie.</p>
 */
@Component({
  selector: 'app-assistance',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, ErrorStateComponent, AssistancePlayerComponent],
  templateUrl: './assistance.component.html',
  styleUrl: './assistance.component.css',
})
export class AssistanceComponent implements OnInit {
  private readonly service = inject(AssistanceService);
  private readonly auth = inject(AuthService);
  private readonly navigation = inject(ExternalNavigationService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly audiences = AUDIENCES;
  readonly audienceIcon = audienceIcon;
  readonly audienceLabel = audienceLabelKey;
  readonly kindIcon = kindIcon;
  readonly kindOf = resourceKind;
  readonly summarize = summarize;

  /** Seule l'équipe choisit le public qu'elle regarde. */
  readonly isStaff = this.auth.isAdmin();
  readonly audience = signal<AssistanceAudience>(this.ownAudience());

  readonly modules = signal<AssistanceModule[]>([]);
  readonly loading = signal(false);
  readonly errored = signal(false);
  readonly query = signal('');
  readonly selectedId = signal<number | null>(null);

  readonly entries = computed<ModuleEntry[]>(() => {
    const q = this.query();
    return this.modules()
      .map((module) => ({
        module,
        items: module.resources.filter((r) => matchesSearch(q, r.title, r.description)),
      }))
      .filter((entry) => entry.items.length > 0);
  });

  /** Le module ouvert — le premier tant qu'aucun n'a été choisi, ou si la recherche l'écarte. */
  readonly current = computed<ModuleEntry | null>(() => {
    const list = this.entries();
    return list.find((entry) => entry.module.id === this.selectedId()) ?? list[0] ?? null;
  });

  readonly playerResource = signal<AssistanceResource | null>(null);
  readonly playerModule = signal<AssistanceModule | null>(null);

  /** Chaque demande de chargement, par public. */
  private readonly requests = new Subject<AssistanceAudience>();

  ngOnInit(): void {
    // switchMap : changer de public annule la requête en cours. Sans lui, une
    // réponse « professeurs » arrivée après la réponse « élèves » affichait les
    // modules des professeurs sous la puce Élèves.
    this.requests
      .pipe(
        switchMap((audience) =>
          this.service.getModules(audience).pipe(
            map((res) => ({ ok: true, data: res?.data ?? [] })),
            catchError(() => of({ ok: false, data: [] as AssistanceModule[] })),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.modules.set(result.data);
        this.errored.set(!result.ok);
        this.loading.set(false);
      });

    if (!this.isStaff) {
      this.load();
      return;
    }

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const requested = params.get('as');
      this.audience.set(isAudience(requested) ? requested : 'Admin');
      this.selectedId.set(null);
      this.query.set('');
      this.load();
    });
  }

  reload(): void {
    this.load();
  }

  private ownAudience(): AssistanceAudience {
    if (this.auth.isAdmin()) return 'Admin';
    return this.auth.isTeacher() ? 'Professor' : 'Student';
  }

  private load(): void {
    this.loading.set(true);
    this.errored.set(false);
    this.requests.next(this.audience());
  }

  /**
   * Rang du module parmi ceux affichés. La position en base compte aussi les
   * modules vides, que la consultation ne reçoit pas : elle laissait des trous.
   */
  position(module: AssistanceModule): number {
    return this.modules().findIndex((m) => m.id === module.id) + 1;
  }

  onOpen(resource: AssistanceResource, module: AssistanceModule): void {
    // Un lien qui ne se lit pas en place s'ouvre à côté, comme avant.
    // Refusé (pas `http(s)`), il tombe dans le lecteur, qui montre son repli.
    if (resourceKind(resource) === 'link' && this.navigation.open(resource.externalUrl)) return;
    this.openPlayer(resource, module);
  }

  openPlayer(resource: AssistanceResource | undefined, module: AssistanceModule): void {
    if (!resource) return;
    this.playerModule.set(module);
    this.playerResource.set(resource);
  }

  closePlayer(): void {
    this.playerResource.set(null);
    this.playerModule.set(null);
  }

  onDownload(resource: AssistanceResource): void {
    this.service.download(resource.id).subscribe({
      next: (res) => {
        const blob = res.body;
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resource.fileName ?? resource.title;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
    });
  }

  trackEntry(_: number, entry: ModuleEntry): number {
    return entry.module.id;
  }

  trackResource(_: number, resource: AssistanceResource): number {
    return resource.id;
  }
}
